import {
  Alert,
  AlertIcon,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { displayDate } from "../../../common/formatting/displayDate";
import {
  concludedDateProblem,
  concludedDuringStandstill,
  todayInOslo,
  type StandstillEnd,
} from "./concludedDate";

interface Props {
  id: string;
  value: string;
  onChange: (value: string) => void;
  earliest: string | null;
  standstill: StandstillEnd | null;
  contractFrom?: string | null;
  helperText?: string;
}

export const ConcludedDateField = ({
  id,
  value,
  onChange,
  earliest,
  standstill,
  contractFrom = null,
  helperText,
}: Props) => {
  const { t } = useTranslation();
  const today = todayInOslo();
  const problem = concludedDateProblem(value, earliest, today, contractFrom);
  return (
    <FormControl isInvalid={problem != null}>
      <FormLabel htmlFor={id}>{t("Date the contract was concluded")}</FormLabel>
      <Input
        id={id}
        type="date"
        backgroundColor="smBackground"
        min={contractFrom ?? earliest ?? undefined}
        max={today}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {problem === "BEFORE_AWARD" && earliest != null && (
        <FormErrorMessage>
          {t(
            "The contract can't have been concluded before the award decision on {{date}}",
            {
              date: displayDate(earliest),
            },
          )}
        </FormErrorMessage>
      )}
      {problem === "BEFORE_INTENTION_NOTICE_WAIT_ENDS" && contractFrom != null && (
        <FormErrorMessage>
          {t(
            "After the intention notice the contract can be concluded from {{date}} (LOA § 13)",
            { date: displayDate(contractFrom) },
          )}
        </FormErrorMessage>
      )}
      {problem === "IN_FUTURE" && (
        <FormErrorMessage>
          {t("The contract can't have been concluded in the future")}
        </FormErrorMessage>
      )}
      {problem == null && helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
      {problem == null &&
        standstill != null &&
        concludedDuringStandstill(value, standstill.lastDay) && (
          <Alert
            status="warning"
            rounded="md"
            fontSize="sm"
            mt="2"
            alignItems="flex-start"
          >
            <AlertIcon />
            {t(
              "The contract was concluded during the standstill period, which ended at the end of {{date}}.",
              {
                date: displayDate(standstill.lastDay),
              },
            )}{" "}
            {standstill.ineffectivenessApplies
              ? t(
                  "A contract concluded before the standstill period has ended can be declared ineffective.",
                )
              : t(
                  "A contract must not be concluded before the standstill period has ended.",
                )}
          </Alert>
        )}
    </FormControl>
  );
};
